import { api } from './api';

// -------------------------------------------------------------------------
// Types matching backend TopicRecommendationDTO
// -------------------------------------------------------------------------

/** Colour category used to render the recommendation chip in the UI. */
export type DisplayChip = 'READY' | 'IN_PROGRESS' | 'STRUGGLING' | 'PREREQUISITE_GAP';

/** Completion status mirroring student_topic_progress.status. */
export type CompletionStatus = 'NOT_STARTED' | 'IN_PROGRESS' | 'COMPLETED';

export interface TopicRecommendation {
  topicId: number;
  topicName: string;
  topicCode: string | null;
  courseId: number;
  courseName: string;
  completionStatus: CompletionStatus;
  lastQuizScore: number | null;
  passThreshold: number;
  passThresholdSource: string;
  recommendationScore: number;
  reason: string;
  /** Names of prerequisite topics the student has not yet completed. */
  prerequisiteGap: string[];
  displayChip: DisplayChip;
  courseTopicOrder: number | null;
}

interface ApiResponse<T> {
  success: boolean;
  message?: string;
  data: T;
}

// -------------------------------------------------------------------------
// Service
// -------------------------------------------------------------------------

class RecommendationService {
  /**
   * Get top recommended topics across all enrolled courses.
   * Calls GET /api/student/recommendations/topics
   */
  async getRecommendedTopics(limit = 10): Promise<TopicRecommendation[]> {
    try {
      const response = await api.get<ApiResponse<TopicRecommendation[]>>(
        '/api/student/recommendations/topics',
        { params: { limit } }
      );
      if (response.data.success) {
        return response.data.data ?? [];
      }
      throw new Error(response.data.message ?? 'Failed to fetch recommendations');
    } catch (error: any) {
      console.error('Error fetching recommendations:', error);
      // Return empty list on error — the UI should degrade gracefully
      return [];
    }
  }

  /**
   * Get recommended topics for a specific course.
   * Calls GET /api/student/recommendations/courses/{courseId}/topics
   */
  async getCourseRecommendations(courseId: number, limit?: number): Promise<TopicRecommendation[]> {
    try {
      const params: Record<string, number> = {};
      if (limit !== undefined) params.limit = limit;

      const response = await api.get<ApiResponse<TopicRecommendation[]>>(
        `/api/student/recommendations/courses/${courseId}/topics`,
        { params }
      );
      if (response.data.success) {
        return response.data.data ?? [];
      }
      throw new Error(response.data.message ?? 'Failed to fetch course recommendations');
    } catch (error: any) {
      console.error('Error fetching course recommendations:', error);
      return [];
    }
  }
}

export const recommendationService = new RecommendationService();
